
import React, { useEffect, useRef, useState } from "react";
import { Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import { Feature } from "ol";
import { Point } from "ol/geom";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { Style, Circle, Fill, Stroke } from "ol/style";
import { Modal, Button } from "react-bootstrap";
import "ol/ol.css";
import "bootstrap/dist/css/bootstrap.min.css";
import solarPumpImage from "./../labPump.jpeg";
import { useAuth } from "../AuthContext";

const IndiaPumpMap = ({ singlePumpLocation }) => {
  const mapRef = useRef();
  const [selectedPump, setSelectedPump] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { pumpLocations } = useAuth();

  useEffect(() => {
    // Determine pump data based on mode
    const isSinglePumpMode = !!singlePumpLocation && singlePumpLocation.geo_location;
    const pumpsToDisplay = isSinglePumpMode ? [singlePumpLocation] : pumpLocations || [];
    
    // Early return if no pumps to display and not in single pump mode
    if (pumpsToDisplay.length === 0) {
      if (isSinglePumpMode) {
        // Single pump mode but no location data - show loading or error
        setIsLoading(false);
        return;
      } else {
        // Dashboard mode - wait for pumpLocations to load
        setIsLoading(true);
        return;
      }
    }
    
    setIsLoading(false);
    
    // Set map center and zoom based on mode
    const mapCenter = isSinglePumpMode
      ? fromLonLat([singlePumpLocation.geo_location[1], singlePumpLocation.geo_location[0]])
      : fromLonLat([78.9629, 20.5937]); // Center on India for dashboard mode
    const mapZoom = isSinglePumpMode ? 10 : 5;

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: mapCenter,
        zoom: mapZoom,
      }),
    });

    // Create pump markers
    const features = pumpsToDisplay.map((pump) => {
      // console.log('hey man this is the pump from pump to display in geoloaction',pump)
      if (!pump?.geo_location || pump.geo_location.length !== 2) {
        return null;
      }

      const feature = new Feature({
        geometry: new Point(fromLonLat([pump.geo_location[1], pump.geo_location[0]])),
        imei: pump.imei,
        status: pump?.pump_online?'Connected':'Disconnected',
        image: solarPumpImage,
      });

      feature.setStyle(
        new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({ color: pump.pump_online ? "green" : "red" }),
            stroke: new Stroke({ color: "white", width: 2 }),
          }),
        })
      );

      return feature;
    }).filter(feature => feature !== null);

    const vectorSource = new VectorSource({ features });
    const vectorLayer = new VectorLayer({ source: vectorSource });
    map.addLayer(vectorLayer);

    // Click event to show modal with pump details
    map.on("click", (event) => {
      const clickedFeature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature);
      if (clickedFeature) {
        setSelectedPump(clickedFeature.getProperties());
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    });

    return () => map.setTarget(undefined); // Cleanup
  }, [singlePumpLocation, pumpLocations]);

  return (
    <>
      {/* The map div must always be in the DOM so mapRef.current is never null
          when the effect runs. The spinner overlays on top while data loads. */}
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        {isLoading && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            zIndex: 10,
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #3498db",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 16px"
              }} />
              <p style={{ margin: 0, color: "#666" }}>Loading pump locations...</p>
            </div>
            <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
          </div>
        )}
      </div>

      {/* Pump Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pump Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={selectedPump?.image}
              alt="Pump"
              width="200px"
              height="200px"
              style={{ padding: "5px" }}
            />
            <p style={{ color: "black" }}>
              <strong>IMEI:</strong> {selectedPump?.imei}
            </p>
            <p style={{ color: "black" }}>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  color: selectedPump?.status  ? "green" : "red",
                }}
              >
                {selectedPump?.status}
              </span>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IndiaPumpMap;